import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, ApiError } from "./api";

// Scheduling in the Growth console. Types mirror the booking app's internal API
// (metrickle/book: src/internal/routes.ts), reached through /admin/book/* (apps/api/src/book/routes.ts),
// and the copy kept from booking events (gtm_booking).

export const BOOKING_SOURCES = ["self", "cold_call", "cold_email", "meetup", "referral", "other"] as const;
export type BookingSource = (typeof BOOKING_SOURCES)[number];
/** The ways Chris books someone in; 'self' is the booking page. */
export const OWNER_SOURCES = ["cold_call", "cold_email", "meetup", "referral", "other"] as const satisfies readonly BookingSource[];

export const SOURCE_LABEL: Record<BookingSource, string> = {
  self: "Booked themselves",
  cold_call: "Cold call",
  cold_email: "Cold email",
  meetup: "Meetup",
  referral: "Referral",
  other: "Other",
};

/** How someone first came to us, for People: "Booked a call", "Met at a meetup". */
export const CHANNEL_LABEL: Record<BookingSource, string> = {
  self: "Booked a call",
  cold_call: "Cold call",
  cold_email: "Cold email",
  meetup: "Met at a meetup",
  referral: "Referral",
  other: "Booked in",
};

export interface BookingJson {
  id: string;
  eventType: { id: string; slug: string; title: string; durationMin: number };
  status: "confirmed" | "cancelled";
  start: string;
  end: string;
  timezone: string;
  name: string | null;
  email: string | null;
  meetUrl: string | null;
  noShow: boolean;
  cancelledAt: string | null;
  cancelledBy: "invitee" | "owner" | null;
  cancelReason: string | null;
  source: BookingSource;
  sourceNote: string | null;
  ref: string | null;
  createdBy: "invitee" | "owner";
  createdAt: string;
  detailsDeleted: boolean;
  emailSuppressed: boolean;
  canChange: boolean;
  started: boolean;
  /** `nurture` after a "Not yet" survey answer. */
  tags: string[];
  followup: Followup | null;
  /** Their survey answer, in short. */
  survey: { usefulness: number; nextStep: NextStep | null } | null;
}

/**
 * The email after the call (the booking app's followup row): `pending` until it goes at `dueAt`
 * (`kind` says which: the survey, or "Sorry we missed each other" for a no-show), then `sent`.
 * `skipped`: cancelled, details deleted or an address that bounced; `off`: the call type sends none.
 */
export interface Followup {
  state: "pending" | "sent" | "skipped" | "cancelled" | "off";
  kind: "survey" | "no_show";
  dueAt: string | null;
  sentAt: string | null;
}

export interface SurveyAnswer {
  usefulness: number | null;
  priority: Priority | null;
  nextStep: NextStep | null;
  note: string | null;
  receivedAt: string;
  /** What it set off, as the booking app routed it. */
  routing: { lowScore: boolean; proposal: boolean; trial: boolean; nurture: boolean } | null;
}

export interface BookingDetail {
  booking: BookingJson;
  answers: { key: string; label: string; value: string | string[] }[];
  history: { at: string; actor: "owner" | "invitee" | "system"; action: string; detail: unknown }[];
  survey: SurveyAnswer | null;
}

// --- The survey after the call (keys from metrickle/book: web/lib/survey.ts) --------------------------

export const PRIORITIES = ["audit", "retainer", "analytics", "exploring"] as const;
export type Priority = (typeof PRIORITIES)[number];
export const NEXT_STEPS = ["proposal", "trial", "not_yet"] as const;
export type NextStep = (typeof NEXT_STEPS)[number];

export const PRIORITY_LABEL: Record<Priority, string> = {
  audit: "Accessibility audit",
  retainer: "Ongoing retainer",
  analytics: "Analytics with Metrickle",
  exploring: "Just exploring",
};

/** What they chose next, as the console says it. */
export const NEXT_STEP_LABEL: Record<NextStep, string> = {
  proposal: "Wants a proposal",
  trial: "Wants a trial",
  not_yet: "Not yet",
};

/** A score this low alerted you at once. */
export const LOW_SCORE = 2;

export interface ResponseRow {
  bookingId: string;
  name: string | null;
  email: string | null;
  detailsDeleted: boolean;
  eventType: { title: string };
  start: string;
  end: string;
  usefulness: number;
  priority: Priority;
  nextStep: NextStep;
  hasNote: boolean;
  receivedAt: string;
}

export interface Responses {
  responses: ResponseRow[];
  nextOffset: number | null;
  totals: { responses: number; surveysSent: number; averageUsefulness: number | null; proposal: number; trial: number; notYet: number; lowScores: number };
  lastReceivedAt: string | null;
}

export interface Question {
  key: string;
  label: string;
  hint: string | null;
  kind: "text" | "textarea" | "single" | "multi";
  options: string[] | null;
  required: boolean;
}

export interface EventType {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  durationMin: number;
  bufferBeforeMin: number;
  bufferAfterMin: number;
  minNoticeMin: number;
  horizonDays: number;
  dailyCap: number;
  slotStepMin: number;
  listed: boolean;
  active: boolean;
  /** The survey 90 minutes after the call. */
  followup: boolean;
  questions: Question[];
}

export interface WeeklyRule {
  weekday: number;
  startMin: number;
  endMin: number;
}

export interface Override {
  id: string;
  date: string;
  kind: "closed" | "hours";
  startMin: number | null;
  endMin: number | null;
  note: string | null;
}

export interface Availability {
  timezone: string | null;
  rules: WeeklyRule[];
  overrides: Override[];
}

export interface BookSettings {
  displayName: string | null;
  notifyEmail: string | null;
  timezone: string | null;
  retentionDays: number;
  brandName: string | null;
  publicOrigin: string;
}

export type CalendarStatus =
  | { mode: "none" | "google"; googleConfigured: boolean; connected: false }
  | {
      mode: "none" | "google";
      googleConfigured: boolean;
      connected: true;
      email: string;
      calendarIds: string[];
      status: "ok" | "failing" | "disconnected";
      failingSince: string | null;
      lastOkAt: string | null;
      lastError: string | null;
    };

export type BookStatus = { connected: true; settings: BookSettings } | { connected: false; reason: string; message: string };

/** A booking from the copy kept here (People, the overview). */
export interface MirrorBooking {
  id: string;
  contactId: string | null;
  name: string | null;
  email: string | null;
  eventType: { slug: string; title: string };
  start: string;
  end: string;
  status: "confirmed" | "cancelled";
  noShow: boolean;
  source: BookingSource;
  ref: string | null;
  createdBy: "invitee" | "owner";
  detailsDeleted: boolean;
  survey: { usefulness: number; nextStep: NextStep | null } | null;
}

export interface Slot {
  start: string;
  end: string;
}

export const BOOK_KEY = ["admin", "book"];

export type BookingView = "upcoming" | "past" | "cancelled" | "no_show";

export const useBookStatus = () => useQuery({ queryKey: [...BOOK_KEY, "status"], queryFn: () => api<BookStatus>("/admin/book/status") });

export function useBookings(params: { view: BookingView; q: string; source: string; offset: number; email?: string }) {
  const qs = new URLSearchParams({ view: params.view, limit: "50", offset: String(params.offset) });
  if (params.q.trim()) qs.set("q", params.q.trim());
  if (params.source !== "all") qs.set("source", params.source);
  if (params.email) qs.set("email", params.email);
  return useQuery({
    queryKey: [...BOOK_KEY, "bookings", qs.toString()],
    queryFn: () => api<{ bookings: BookingJson[]; nextOffset: number | null; now: string }>(`/admin/book/bookings?${qs}`),
    placeholderData: keepPreviousData,
    retry: retryUnlessNotConnected,
  });
}

export function useResponses(offset: number) {
  return useQuery({
    queryKey: [...BOOK_KEY, "responses", offset],
    queryFn: () => api<Responses>(`/admin/book/responses?limit=50&offset=${offset}`),
    placeholderData: keepPreviousData,
    retry: retryUnlessNotConnected,
  });
}

export const useBooking = (id: string) =>
  useQuery({ queryKey: [...BOOK_KEY, "booking", id], queryFn: () => api<BookingDetail>(`/admin/book/bookings/${encodeURIComponent(id)}`), retry: retryUnlessNotConnected });
export const useEventTypes = (enabled = true) =>
  useQuery({ queryKey: [...BOOK_KEY, "event-types"], queryFn: () => api<{ publicOrigin: string; eventTypes: EventType[] }>("/admin/book/event-types"), enabled, retry: retryUnlessNotConnected });
export const useAvailability = () => useQuery({ queryKey: [...BOOK_KEY, "availability"], queryFn: () => api<Availability>("/admin/book/availability"), retry: retryUnlessNotConnected });
export const useBookSettings = () => useQuery({ queryKey: [...BOOK_KEY, "settings"], queryFn: () => api<BookSettings>("/admin/book/settings"), retry: retryUnlessNotConnected });
export const useCalendar = () => useQuery({ queryKey: [...BOOK_KEY, "calendar"], queryFn: () => api<CalendarStatus>("/admin/book/calendar"), retry: retryUnlessNotConnected });
export const useUpcomingCalls = () => useQuery({ queryKey: [...BOOK_KEY, "mirror", "upcoming"], queryFn: () => api<{ bookings: MirrorBooking[] }>("/admin/book/mirror/upcoming?limit=5") });
export const usePersonBookings = (contactId: string) =>
  useQuery({ queryKey: [...BOOK_KEY, "mirror", "person", contactId], queryFn: () => api<{ bookings: MirrorBooking[] }>(`/admin/book/mirror/people/${encodeURIComponent(contactId)}`) });

/** Free times the owner may book for a call type in [from, to); `bookingId` leaves that booking's own time free (moving it). */
export function useOwnerSlots(eventTypeId: string | null, from: string, to: string, bookingId?: string) {
  const qs = new URLSearchParams({ from, to, ...(bookingId ? { bookingId } : {}) });
  return useQuery({
    queryKey: [...BOOK_KEY, "slots", eventTypeId, qs.toString()],
    queryFn: () => api<{ now: string; slots: Slot[] }>(`/admin/book/event-types/${encodeURIComponent(eventTypeId!)}/slots?${qs}`),
    enabled: !!eventTypeId,
    retry: retryUnlessNotConnected,
  });
}

/** The API's answer when the booking app can't be reached, with the reason in words. */
export function notConnected(err: unknown): { reason: string; message: string } | null {
  if (!(err instanceof ApiError) || err.status !== 503) return null;
  const data = err.data as { error?: string; reason?: string; message?: string } | undefined;
  return data?.error === "book_not_connected" ? { reason: data.reason ?? "unknown", message: data.message ?? "" } : null;
}

const retryUnlessNotConnected = (count: number, err: unknown) => count < 1 && !notConnected(err) && !(err instanceof ApiError && err.status < 500);

/** The booking app's field problems from a 400 (`{ fields: { name: "Enter their name" } }`). */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || (err.status !== 400 && err.status !== 409)) return {};
  const fields = (err.data as { fields?: unknown } | undefined)?.fields;
  return fields && typeof fields === "object" ? (fields as Record<string, string>) : {};
}

/** Why a time couldn't be booked or moved to, in words. Null when it isn't one of those. */
export function slotProblem(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const code = (err.data as { error?: string } | undefined)?.error;
  if (code === "slot_taken") return "That time was just taken. Choose another.";
  if (code === "day_full") return "That day is full for this call type. Choose another day.";
  if (code === "not_bookable") return "That time isn't in your hours any more. Choose another.";
  if (code === "booking_paused") return "Booking is paused: the calendar can't be read. Check the connection in Scheduling.";
  if (code === "started") return "The call has already started, so it can't be changed.";
  if (code === "cancelled") return "This call is already cancelled.";
  return null;
}

// --- Time ------------------------------------------------------------------------------------

/** "Tuesday 13 October" in a zone. */
export const dayLabel = (iso: string, tz: string) => dateParts(iso, tz, "long");
/** "10:00" in a zone, 24-hour. */
export const timeLabel = (iso: string, tz: string) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));
/** "Tue 13 Oct" in a zone. */
export const dateShort = (iso: string, tz: string) => dateParts(iso, tz, "short");
/** "Tue 13 Oct, 10:00 to 10:30" in a zone. */
export function whenShort(start: string, end: string, tz: string): string {
  return `${dateShort(start, tz)}, ${timeLabel(start, tz)} to ${timeLabel(end, tz)}`;
}

/** Weekday, day and month joined by spaces: ICU versions disagree on a comma after the weekday. */
function dateParts(iso: string, tz: string, width: "long" | "short"): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: width, day: "numeric", month: width }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("weekday")} ${get("day")} ${get("month")}`;
}
/** The calendar date of an instant in a zone, YYYY-MM-DD. */
export const dateKey = (iso: string, tz: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));

/** Slots grouped by the day they start on in `tz`, in time order. */
export function groupSlotsByDay(slots: Slot[], tz: string): { date: string; label: string; slots: Slot[] }[] {
  const days = new Map<string, Slot[]>();
  for (const s of [...slots].sort((a, b) => Date.parse(a.start) - Date.parse(b.start))) {
    const k = dateKey(s.start, tz);
    days.set(k, [...(days.get(k) ?? []), s]);
  }
  return [...days].map(([date, list]) => ({ date, label: dayLabel(list[0]!.start, tz), slots: list }));
}

/** Minutes after midnight as "09:30". */
export const minutesToTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
/** "09:30" as minutes after midnight; "24:00" (or "00:00" as an end) is 1440. NaN when it isn't a time. */
export function timeToMinutes(t: string, end = false): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
  if (!m) return Number.NaN;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59 || (h === 24 && min > 0)) return Number.NaN;
  const total = h * 60 + min;
  return end && total === 0 ? 1440 : total;
}

/** ISO weekday 1 (Monday) to 7. */
export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Every zone the browser knows, with `current` kept in the list. */
export function timeZoneOptions(current: string | null): { value: string; label: string }[] {
  let list: string[] = [];
  try {
    list = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  } catch {
    list = [];
  }
  if (current && !list.includes(current)) list = [current, ...list];
  return list.map((z) => ({ value: z, label: z.replace(/_/g, " ") }));
}

// --- Links -------------------------------------------------------------------------------------

/** A `?src=` tag from free text: "Brighton a11y, Oct" → "brighton-a11y-oct". The booking app keeps 64 characters of a-z, 0-9 and hyphens. */
export function srcTag(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
    .replace(/-+$/, "");
}

/** A call type's booking link, tagged when there's a tag. */
export function bookingLink(origin: string, slug: string, tag = ""): string {
  const url = `${origin.replace(/\/$/, "")}/${encodeURIComponent(slug)}`;
  const t = srcTag(tag);
  return t ? `${url}?src=${t}` : url;
}
